import React, { useEffect, useRef } from 'react';
import { useAccessibility } from '../context/AccessibilityContext';
import { useLanguage } from '../context/LanguageContext';
import './AccessibilitySettings.css';

function AccessibilitySettings({ isOpen, onClose }) {
  const { t } = useLanguage();
  const { settings, setSettings, reset } = useAccessibility();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    const firstFocusable = panelRef.current?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="accessibility-panel-wrap"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accessibility-panel-title"
      aria-describedby="accessibility-panel-desc"
    >
      <div className="accessibility-panel-overlay" onClick={onClose} aria-hidden="true" />
      <div className="accessibility-panel" ref={panelRef} role="document">
        <div className="accessibility-panel-header">
          <h2 id="accessibility-panel-title" className="accessibility-panel-title">
            {t('accessibilitySettingsTitle')}
          </h2>
          <p id="accessibility-panel-desc" className="accessibility-panel-desc">
            {t('accessibilitySettingsDesc')}
          </p>
          <button
            type="button"
            className="accessibility-panel-close"
            onClick={onClose}
            aria-label={t('closeAccessibility')}
          >
            ×
          </button>
        </div>

        <div className="accessibility-panel-body">
          <fieldset className="accessibility-fieldset">
            <legend className="accessibility-legend">{t('textSize')}</legend>
            <div className="accessibility-options" role="group">
              {[
                { value: 'normal', labelKey: 'normalSize' },
                { value: 'large', labelKey: 'largerSize' },
                { value: 'larger', labelKey: 'evenLargerSize' },
              ].map(({ value, labelKey }) => (
                <label key={value} className="accessibility-option">
                  <input
                    type="radio"
                    name="fontSize"
                    value={value}
                    checked={settings.fontSize === value}
                    onChange={() => setSettings({ fontSize: value })}
                  />
                  <span>{t(labelKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="accessibility-fieldset">
            <legend className="accessibility-legend">{t('contrast')}</legend>
            <div className="accessibility-options" role="group">
              {[
                { value: 'normal', labelKey: 'normalSize' },
                { value: 'high', labelKey: 'highContrast' },
              ].map(({ value, labelKey }) => (
                <label key={value} className="accessibility-option">
                  <input
                    type="radio"
                    name="contrast"
                    value={value}
                    checked={settings.contrast === value}
                    onChange={() => setSettings({ contrast: value })}
                  />
                  <span>{t(labelKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="accessibility-toggles">
            <label className="accessibility-toggle">
              <input
                type="checkbox"
                checked={settings.reduceMotion}
                onChange={(e) => setSettings({ reduceMotion: e.target.checked })}
              />
              <span>{t('reduceMotion')}</span>
            </label>
            <label className="accessibility-toggle">
              <input
                type="checkbox"
                checked={settings.focusVisible}
                onChange={(e) => setSettings({ focusVisible: e.target.checked })}
              />
              <span>{t('showFocusOutline')}</span>
            </label>
            <label className="accessibility-toggle">
              <input
                type="checkbox"
                checked={settings.underlineLinks}
                onChange={(e) => setSettings({ underlineLinks: e.target.checked })}
              />
              <span>{t('underlineLinks')}</span>
            </label>
          </div>

          <div className="accessibility-actions">
            <button type="button" className="btn btn-secondary" onClick={reset}>
              {t('resetSettings')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessibilitySettings;
